// UC50 Operator manual reporting: "Raport nou".
//
// The frame is the wireframe's committed direction C
// (wireframes/react/src/pages/raport-nou/VariantaC.tsx, "direcția angajată"):
// ONE report card whose toolbar carries the required storage location on the
// left and the report's own search and filters on the right (piese.tsx:1325
// `BaraUnelte`), a table that only lists rows (piese.tsx:1226 `TabelDraft`), and
// a footer that counts the report and holds its two actions (piese.tsx:1434
// `SubsolRaport`). A movement is entered in a dialog, "Adaugă mișcare de stoc"
// (StockMovementDialog), opened from the header, from the empty table, or with
// the N key (spec 054 User Story 1).
//
// The draft starts at its first row (FR-023): visiting the page creates nothing.
// Rows may be added before a storage location is chosen; the location is asked
// for when the report is saved (owner decision, 2026-09-15, replacing research
// R2's "a location before the first movement"): "Salvează raport" then opens no
// confirmation, marks the location field and says so.
// Opened from "Raporturi nefinalizate" at `/:role/reporting/drafts/:draftId`, the
// page is that draft, titled "Finalizează raport" (spec 054 User Story 5). With
// `?corectie={stockRowId}` it opens the dialog in correction mode on the
// movement's own location (User Story 6, research R9).
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import {
  MudButton,
  MudCheckbox,
  MudIcon,
  MudInlineMessage,
  MudModal,
  MudInfoBox,
  MudSelect,
  MudTable,
  MudTag,
  MudTooltip,
} from '@egov-moldova/mud-react';
import { stockMovementReasonSchema, type StockMovementReason } from '@simsm/shared-types';
import {
  Breadcrumb,
  EMPTY_CELL,
  EmptyState,
  FilterBar,
  formatDate,
  formatExpiry,
  formatNumber,
  formatTime,
  getDateLocale,
  onModalDismiss,
  PageHeader,
  RowActionsMenu,
  useToast,
} from '@simsm/ui-shared';
import type { SavedReportState } from '../../../features/reporting/saved-report';
import {
  getMovement,
  type DraftRow,
  type MovementForCorrection,
} from '../../../features/reporting/api';
import { apiErrorKey, isNormalizedApiError, loadFailureKind } from '../../../app/api-client';
import { useDraft } from '../../../features/reporting/use-draft';
import { useReportingLocations } from '../../../features/reporting/use-locations';
import {
  filterReportRows,
  isCorrectionRow,
  lacksMedicineName,
  rowCounts,
  shownReportRows,
  type ReportRowCriteria,
} from '../../../features/reporting/report-rows';
import { useMedicineNames } from '../../../features/reporting/medicine-identity';
import { CorrectionReference } from '../../../features/reporting/correction-target';
import { rowExpiryPrecision, rowIssueMessage } from '../../../features/reporting/row-messages';
import {
  defaultDirectionOf,
  defaultsOf,
  reasonLabelKey,
  rememberReason,
  startingDefaultReasons,
} from '../../../features/reporting/movement-form';
import { useSession } from '../../../hooks/use-session';
import { DefaultReasonsDialog } from './DefaultReasonsDialog';
import { StockMovementDialog, type MovementAdded } from './StockMovementDialog';
import './stock-reporting.css';

interface SelectOption {
  value: string;
  label: string;
}

interface SelectChangeDetail {
  value: string;
}

// mud-table's own column type is not re-exported from the package root.
interface TableColumn {
  key: string;
  label: string;
  align?: 'start' | 'center' | 'end';
  width?: string;
}

const NO_CRITERIA: ReportRowCriteria = {
  query: '',
  errorsOnly: false,
  newLotsOnly: false,
  correctionsOnly: false,
};

type TFn = ReturnType<typeof useTranslate>['t'];

// Why the confirmation was refused, as a Tolgee key. The mapping itself lives in
// `apiErrorKey`, the frontoffice's one code-to-copy table. The 404 is read from
// the status because core-service throws it with no domain code. An inactive
// Registrar refuses the report here (UC10-10, TC-UC10-10-01), and the table's
// sentence for that code is the credentials screen's.
function confirmErrorKey(err: unknown): string {
  if (isNormalizedApiError(err) && err.status === 404)
    return 'stock_reporting.error.not_found.body';
  if (isNormalizedApiError(err) && err.code === 'REGISTRAR_INACTIVE')
    return 'stock_reporting.error.registrar_inactive.body';
  return apiErrorKey(err, 'stock_reporting.error.generic.body');
}

// "Typing" is read off the element the key was really typed into (research
// R12): a key typed in a MUD field reaches the document retargeted to the
// `MUD-TEXT-INPUT` host (app/dev-input-guard.ts), so `event.target` says nothing
// useful. `composedPath()[0]` is the inner control of an open shadow root, and
// the host itself when the key starts there.
const TEXT_HOSTS =
  /^MUD-(TEXT-INPUT|NUMERIC-INPUT|SEARCH-INPUT|TEXTAREA|SELECT|DATE-INPUT|DATE-PICKER|PHONE-INPUT)$/;

function isTyping(event: KeyboardEvent): boolean {
  const origin = event.composedPath()[0];
  if (!(origin instanceof HTMLElement)) return false;
  if (origin.isContentEditable) return true;
  if (origin.closest('[contenteditable]:not([contenteditable="false"])')) return true;
  if (origin.tagName === 'INPUT' || origin.tagName === 'TEXTAREA' || origin.tagName === 'SELECT')
    return true;
  return TEXT_HOSTS.test(origin.tagName);
}

// The control inside a MUD host: mud-select's is its `role="combobox"`,
// mud-button's its button.
function focusControl(element: Element | null | undefined): void {
  if (!(element instanceof HTMLElement)) return;
  const root = element.shadowRoot;
  const inner =
    root?.querySelector<HTMLElement>('[role="combobox"]') ??
    root?.querySelector<HTMLElement>('button, input');
  (inner ?? element).focus();
}

// The shared formatDate says "Astăzi" / "Ieri" (MUD's words for today and
// yesterday), capitalised because a register cell stands alone. After
// "Început" it is mid-sentence, so those words are lowered: they are the only
// forms with no digit in them, in every locale ("Today", "Сегодня"), so a
// month name such as "Sep 15" keeps its capital.
function midSentenceDate(iso: string): string {
  const date = formatDate(iso);
  return /\d/.test(date) ? date : date.toLocaleLowerCase();
}

const INTL_LOCALE = { ro: 'ro-RO', ru: 'ru-RU', en: 'en-US' } as const;

// The save confirmation's "Data și ora salvării" is a date written out, "14
// septembrie 2026", as the wireframe's DialogSalvare writes it (`fmtDateFull`),
// never the register's "Astăzi" (spec 054 T100): it is the record of when, read
// again later. In the order each language writes it, as the shared formatter does.
function fullDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const locale = getDateLocale();
  const parts = new Intl.DateTimeFormat(INTL_LOCALE[locale], {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((entry) => entry.type === type)?.value ?? '';
  return locale === 'en'
    ? `${part('month')} ${part('day')}, ${part('year')}`
    : `${part('day')} ${part('month')} ${part('year')}`;
}

/** new − previous, when the row carries both (research R1). */
function differenceOf(row: DraftRow): number | null {
  if (typeof row.currentQuantity !== 'number' || typeof row.previousQuantity !== 'number')
    return null;
  return row.currentQuantity - row.previousQuantity;
}

/**
 * What another address hands this one in the router state: the location to
 * open (leaving a draft for another location's report), and whether that came
 * from a refused bind (SITE_HAS_DRAFT). Read as untrusted input: history state
 * outlives the page that wrote it.
 */
interface ReportHandOff {
  siteId?: string;
  siteHasDraft?: boolean;
}

function readHandOff(state: unknown): ReportHandOff {
  if (typeof state !== 'object' || state === null) return {};
  const candidate = state as Record<string, unknown>;
  const siteId = candidate['siteId'];
  return {
    ...(typeof siteId === 'string' && siteId !== '' ? { siteId } : {}),
    ...(candidate['siteHasDraft'] === true ? { siteHasDraft: true } : {}),
  };
}

export interface StockReportingPageProps {
  /** The draft `/:role/reporting/drafts/:draftId` names; absent on "Raport nou". */
  draftId?: string | undefined;
}

export function StockReportingPage({ draftId }: StockReportingPageProps = {}): JSX.Element {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const toast = useToast();
  const location = useLocation();
  // This page is mounted under both role rails (app/router.tsx), and the import
  // screen sits under whichever one the operator came in by.
  const rolePrefix = location.pathname.startsWith('/registrar-admin')
    ? '/registrar-admin'
    : '/operator';
  const session = useSession().data;
  const [searchParams, setSearchParams] = useSearchParams();
  // The movement "Corectează mișcarea" names (research R9); a draft opened by
  // its id is finished, not corrected into.
  const correctionId = draftId ? null : searchParams.get('corectie');
  const [handOff] = useState(() => readHandOff(location.state));

  const reasonLabel = (row: DraftRow): string => {
    if (!row.reason) return '';
    const parsed = stockMovementReasonSchema.safeParse(row.reason);
    return parsed.success ? t(reasonLabelKey(parsed.data)) : row.reason;
  };

  // With the locations comes the active Registrar's type, which decides the reasons
  // the dialog offers and starts from (UC50-15; docs/shape-ops-contract-codes.md
  // §Decision 7). The session's distinct kinds cannot say which Registrar this is.
  // Until the route answers, the dialog offers no reason (§Decision 8).
  const {
    locations,
    registrarKind,
    registrarKindState,
    isLoading: locationsLoading,
    isError: locationsFailed,
  } = useReportingLocations();
  // The location the report is for. Null until one is chosen. Rows may be
  // added meanwhile, their previous stock unknown until then (R2); the report
  // cannot be saved without one (owner decision, 2026-09-15). On the drafts
  // route it is the draft's own, once the draft has loaded.
  const [siteId, setSiteId] = useState<string | null>(draftId ? null : (handOff.siteId ?? null));
  const [pendingSiteId, setPendingSiteId] = useState<string | null>(null);
  const [siteHasDraftNotice, setSiteHasDraftNotice] = useState(handOff.siteHasDraft === true);
  // "Salvează raport" pressed with no location: the field is marked, and the
  // notice says why, until a location is chosen.
  const [saveNeedsLocation, setSaveNeedsLocation] = useState(false);
  const locationFieldRef = useRef<HTMLDivElement>(null);
  const {
    draft,
    rows,
    isLoading,
    isError,
    error,
    syncState,
    serverRows,
    unsaved,
    addRow,
    replaceRow,
    removeRow,
    conflict,
    dismissConflict,
    retrySave,
    discardMutation,
    defaultReasons,
    rememberDefaultReasons,
    confirmMutation,
    bindSiteMutation,
  } = useDraft(siteId, draftId ?? null);
  // A draft id the bff answers 404 for: gone, or not this operator's (the same
  // answer for both, constitution art. II).
  const draftMissing = Boolean(draftId) && !isLoading && !isError && draft === null;

  const [criteria, setCriteria] = useState<ReportRowCriteria>(NO_CRITERIA);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  // "Motive implicite" (caiet UC50 step 2), a fresh panel per opening, on the
  // defaults in force then.
  const [defaultsOpen, setDefaultsOpen] = useState(false);
  const [defaultsKey, setDefaultsKey] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  // A fresh dialog per opening: it starts on an empty search every time.
  const [dialogKey, setDialogKey] = useState(0);
  // The row "Editează" opened; null when the dialog adds (FR-020).
  const [editingId, setEditingId] = useState<string | null>(null);
  // An edit a concurrent save could not re-apply, offered again (research R7).
  const [prefill, setPrefill] = useState<DraftRow | null>(null);
  // The movement the dialog corrects (User Story 6); null otherwise.
  const [correcting, setCorrecting] = useState<MovementForCorrection | null>(null);
  // The "Mișcare adăugată" / "Rând modificat" toasts of rows whose save has not
  // answered yet (T101): shown once it has, dropped if it fails.
  const [pendingRowToasts, setPendingRowToasts] = useState<
    { kind: 'added' | 'edited'; clientRowId: string; medicine: string; lot: string }[]
  >([]);
  const addButtonRef = useRef<HTMLSpanElement>(null);
  const dialogWasOpen = useRef(false);
  // Where the focus goes back to once the dialog closes: the row's own menu
  // after an edit; "Adaugă mișcare" after an add (FR-009); otherwise the button
  // that opened it — the empty state's own, when that was the one pressed.
  const returnToRow = useRef<string | null>(null);
  const openedFrom = useRef<HTMLElement | null>(null);
  const closedByAdd = useRef(false);
  // "Adaugă mișcare (N)" opens on focus as well as on hover — AGE pairs the two
  // (WCAG 1.4.13). Focus the page hands back after the dialog is not the reader
  // asking for the hint, so it stays quiet until the focus or the pointer
  // leaves the button (owner report 2026-09-15).
  const [quietAddHint, setQuietAddHint] = useState(false);

  // T099: an imported row names its medicine by code alone; the catalogue
  // names it, for display (the draft is not rewritten behind the operator).
  const medicineNames = useMedicineNames(
    siteId,
    rows.filter(lacksMedicineName).map((row) => row.medicineCode ?? ''),
  );
  // T097, constitution art. IV: the rows on screen, each with the errors the
  // server holds for it. The count of rows with errors is the server's, so a
  // save that failed never reads as a report without errors.
  const shownRows = shownReportRows(rows, serverRows, medicineNames);
  const counts = { ...rowCounts(shownRows), withErrors: rowCounts(serverRows).withErrors };
  // Not memoised: the answer depends on the label, which closes over Tolgee's
  // `t`, and the report is tens of rows long.
  const filteredRows = filterReportRows(shownRows, criteria, (reason: StockMovementReason) =>
    t(reasonLabelKey(reason)),
  );
  const editingRow = editingId ? rows.find((row) => row.clientRowId === editingId) : undefined;
  // Null until the active Registrar's type is known: the dialog then holds no reason.
  const defaults = useMemo(
    () =>
      registrarKind
        ? defaultsOf(defaultReasons, startingDefaultReasons(registrarKind), registrarKind)
        : null,
    [defaultReasons, registrarKind],
  );
  // The direction a new movement starts from before its level is typed
  // (docs/shape-ops-default-reasons.md §Decision 3).
  const defaultDirection = defaultDirectionOf(defaultReasons);

  /**
   * The rule the whole location control turns on (specs/028-raport-nou-layout
   * §Q1): rows never cross a location. A draft bound to no location (from
   * before spec 054, or the import) is BOUND to the one picked, rows and all.
   * Any other change of location opens that location's own report, leaving
   * these rows where they were entered, and asks first when there are any.
   * Rows the browser holds for a report that has no location yet were entered
   * for no location: they go to the one picked (use-draft), no question asked.
   */
  function chooseSite(next: string): void {
    if (next === '' || next === siteId) return;
    setSiteHasDraftNotice(false);
    setSaveNeedsLocation(false);
    if (siteId === null && draft && !draft.siteId && draft.rows.length > 0) {
      bindSiteMutation.mutate(next, {
        // A location that already has a report opens that one rather than
        // taking these rows (contracts/bff-stock-reporting.md, PATCH; T063):
        // use-draft has loaded it, and the unbound draft keeps its rows.
        onSuccess: ({ draft: opened, openedExisting }) => {
          if (openedExisting) setSiteHasDraftNotice(true);
          if (draftId && opened.id !== draftId) {
            navigate(`${rolePrefix}/reporting/drafts/${opened.id}`, {
              state: { siteHasDraft: openedExisting },
            });
            return;
          }
          setSiteId(next);
        },
      });
      return;
    }
    if (siteId !== null && rows.length > 0) {
      setPendingSiteId(next);
      return;
    }
    openSite(next);
  }

  // On the drafts route the page IS that draft: another location is another
  // report, at its own address (spec 028 Q1).
  function openSite(next: string): void {
    if (draftId) {
      navigate(`${rolePrefix}/reporting/new`, { state: { siteId: next } });
      return;
    }
    setSiteId(next);
  }

  // A location however chosen answers the refusal at save.
  useEffect(() => {
    if (siteId !== null) setSaveNeedsLocation(false);
  }, [siteId]);

  // A draft opened by its id names its location (FR-024).
  useEffect(() => {
    if (draftId && draft?.siteId && siteId === null) setSiteId(draft.siteId);
  }, [draftId, draft, siteId]);

  // A draft deleted elsewhere while this tab still had rows to save is started
  // again under a new id (use-draft, R7): the address follows it.
  useEffect(() => {
    if (draftId && draft && draft.id !== draftId)
      navigate(`${rolePrefix}/reporting/drafts/${draft.id}`, { replace: true });
  }, [draftId, draft]);

  // Caiet UC50 flux step 3: "aceasta fiind automat selectată dacă este una
  // singură asociată Operatorului". Waits for the unbound-draft lookup, so a
  // draft with no location is bound rather than left behind. A draft opened by
  // its id names its own location, and a correction the movement's.
  const autoPicked = useRef(false);
  useEffect(() => {
    if (autoPicked.current || siteId !== null || isLoading || locations.length !== 1) return;
    if (correctionId) return;
    if (draftId && !(draft && !draft.siteId)) return;
    autoPicked.current = true;
    chooseSite(locations[0]!.id);
  }, [locations, isLoading, siteId, draft, correctionId]);

  // ── Correcting a reported movement (User Story 6, research R9) ──────────────
  const movementQuery = useQuery({
    queryKey: ['stock-movement', correctionId],
    queryFn: () => getMovement(correctionId ?? ''),
    enabled: Boolean(correctionId),
    retry: false,
    staleTime: Infinity,
  });
  const movement = correctionId ? movementQuery.data : undefined;
  const movementSiteKnown = movement
    ? locations.some((option) => option.id === movement.siteId)
    : false;
  let correctionFailure: string | null = null;
  if (correctionId && movementQuery.isError)
    correctionFailure = 'stock_reporting.correction.load_error';
  else if (movement && !locationsLoading && !movementSiteKnown)
    correctionFailure = 'stock_reporting.correction.foreign_site';

  // The report is bound to the movement's own location, since a correction may
  // not cross one (CORRECTION_MISMATCH); then the dialog opens on it, once.
  // The page has just opened, so no row of another location is in the browser:
  // that location's draft keeps its rows.
  const correctionOpened = useRef(false);
  useEffect(() => {
    if (!movement || !movementSiteKnown || correctionOpened.current) return;
    if (siteId !== movement.siteId) {
      setSiteId(movement.siteId);
      return;
    }
    if (isLoading || dialogOpen) return;
    correctionOpened.current = true;
    setEditingId(null);
    setPrefill(null);
    setCorrecting(movement);
    returnToRow.current = null;
    setDialogKey((key) => key + 1);
    setDialogOpen(true);
  }, [movement, movementSiteKnown, siteId, isLoading, dialogOpen]);

  // The event is the press that opened it, which is where the focus goes back
  // to; the N key passes none.
  function openDialog(event?: { currentTarget: EventTarget | null }): void {
    if (dialogOpen) return;
    setEditingId(null);
    setPrefill(null);
    setCorrecting(null);
    returnToRow.current = null;
    openedFrom.current = event?.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    setDialogKey((key) => key + 1);
    setDialogOpen(true);
  }

  // "Editează", and "Editează rândul existent" from inside the dialog, which
  // puts the row a duplicate repeats in the dialog's place (FR-016, FR-020).
  function openEdit(clientRowId: string): void {
    setEditingId(clientRowId);
    setPrefill(null);
    setCorrecting(null);
    returnToRow.current = clientRowId;
    setDialogKey((key) => key + 1);
    setDialogOpen(true);
  }

  // "Adaugă din nou": the edit another tab's removal left without a row, back
  // in the dialog as it was entered (research R7, constitution art. III).
  function restoreConflict(): void {
    if (!conflict) return;
    const kept = conflict.row;
    dismissConflict();
    setEditingId(null);
    setCorrecting(null);
    setPrefill(kept);
    returnToRow.current = null;
    setDialogKey((key) => key + 1);
    setDialogOpen(true);
  }

  function closeDialog(): void {
    setDialogOpen(false);
    setEditingId(null);
    setPrefill(null);
    setCorrecting(null);
    // The correction was offered once; a reload must not offer it again.
    if (searchParams.has('corectie')) {
      const next = new URLSearchParams(searchParams);
      next.delete('corectie');
      setSearchParams(next, { replace: true });
    }
  }

  function onMovementAdded(added: MovementAdded): void {
    // FR-007: the reason just used is its direction's default for the next row.
    // Before the type is known there are no defaults, and the one movement the
    // dialog adds then, a correction, is nobody's default.
    // The direction goes with the two reasons, so remembering one cannot drop
    // the direction "Motive implicite" set (docs/shape-ops-default-reasons.md
    // §Decision 5).
    if (defaults) {
      const next = rememberReason(defaults, added.reason, added.sign);
      if (next !== defaults) rememberDefaultReasons({ ...next, direction: defaultDirection });
    }
    // An edit replaces its row where it stands: the row never left the draft
    // while the dialog was open (FR-025).
    // A row removed meanwhile is added back rather than lost (constitution art. III).
    const edited = editingRow !== undefined;
    if (edited) replaceRow(added.row);
    else addRow(added.row);
    // FR-009: after an add the focus goes to "Adaugă mișcare", whichever button
    // opened the dialog — the empty state's own is about to give way to the row.
    closedByAdd.current = !edited;
    closeDialog();
    setPendingRowToasts((queued) => [
      ...queued,
      {
        kind: edited ? 'edited' : 'added',
        clientRowId: added.row.clientRowId,
        medicine: added.medicineLabel,
        lot: added.lot,
      },
    ]);
  }

  // The row toast says the report holds the row, so it waits for the save that
  // carries it: the create (POST) of a report's first row, the debounced PATCH
  // of any later one (T101; constitution art. IV, no success the server does
  // not hold). A save that fails drops it, since the "nu s-a salvat" notice
  // already speaks; a later success after "Reîncearcă" does not bring it back:
  // the notice going away is that confirmation. Keyed on the sync state itself,
  // so only a failure after the toast was queued counts, not an older one.
  useEffect(() => {
    if (syncState.kind === 'error') setPendingRowToasts([]);
  }, [syncState]);

  // Shown once nothing on screen waits for the server, which then holds every
  // queued row, and after the dialog closes (never inside it, whose top layer
  // would cover it; R12). An edit handed back as a conflict was not saved, and
  // a report emptied meanwhile holds none of them.
  useEffect(() => {
    if (pendingRowToasts.length === 0 || dialogOpen || unsaved) return;
    setPendingRowToasts([]);
    if (!draft) return;
    for (const queued of pendingRowToasts) {
      if (queued.clientRowId === conflict?.row.clientRowId) continue;
      toast({
        variant: 'success',
        title: t(
          queued.kind === 'edited'
            ? 'stock_reporting.toast.edited.title'
            : 'stock_reporting.toast.added.title',
        ),
        text: t('stock_reporting.toast.added.text', {
          medicine: queued.medicine,
          lot: queued.lot,
        }),
      });
    }
  }, [pendingRowToasts, dialogOpen, unsaved, draft, conflict]);

  // After the dialog closes, at once and whatever the save does: the focus back
  // where the dialog was opened from, the row's menu or "Adaugă mișcare" (FR-009).
  useEffect(() => {
    if (dialogOpen) {
      dialogWasOpen.current = true;
      // Quiet from the moment the dialog opens, by whichever route, so the
      // prop is long in place by the time the focus comes back.
      setQuietAddHint(true);
      return undefined;
    }
    if (!dialogWasOpen.current) return undefined;
    dialogWasOpen.current = false;
    const rowId = returnToRow.current;
    const opener = closedByAdd.current ? null : openedFrom.current;
    returnToRow.current = null;
    openedFrom.current = null;
    closedByAdd.current = false;
    const id = setTimeout(() => {
      const rowMenu = [...document.querySelectorAll<HTMLElement>('[data-row-actions]')].find(
        (cell) => cell.dataset['rowActions'] === rowId,
      );
      const addButton = addButtonRef.current?.querySelector('mud-button, button') ?? null;
      const target =
        rowMenu?.querySelector('mud-button, button') ??
        (opener?.isConnected ? opener : null) ??
        addButton;
      // Only the focus handed to "Adaugă mișcare" itself needs its hint muted.
      if (target !== addButton) setQuietAddHint(false);
      focusControl(target);
    }, 0);
    return () => clearTimeout(id);
  }, [dialogOpen]);

  // N opens the dialog from anywhere on the page, except while typing
  // (VariantaC.tsx; FR-001). Subscribed per render so it reads the current state.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key !== 'n' && event.key !== 'N') return;
      if (event.altKey || event.ctrlKey || event.metaKey || event.repeat) return;
      if (event.defaultPrevented || dialogOpen || confirmOpen || clearOpen || defaultsOpen) return;
      if (pendingSiteId !== null) return;
      if (isTyping(event)) return;
      event.preventDefault();
      openDialog();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  });

  // ── Saving (spec 054 User Story 2, caiet UC50-10) ─────────────────────────────
  // When the confirmation opened: its "Data și ora salvării".
  const [confirmOpenedAt, setConfirmOpenedAt] = useState('');
  // "Raportat de" is the person saving, whom the submission records as its
  // reporter (spec 030), not necessarily the one who started the draft.
  const reporterName = session
    ? `${session.firstName} ${session.lastName}`.trim()
    : (draft?.operatorName ?? '');
  // FR-028: the summary names the DRAFT's own location, never whatever the
  // picker shows (a wireframe defect spec 054 does not copy).
  const draftLocationName = locations.find((location) => location.id === draft?.siteId)?.name ?? '';

  function openConfirmation(): void {
    // The asterisk on "Locație de stocare *" made real, at save (owner decision,
    // 2026-09-15): no confirmation for a report with no location. The field is
    // marked and takes the focus, and the notice beside the footer says why.
    if (siteId === null) {
      setSaveNeedsLocation(true);
      setTimeout(
        () => focusControl(locationFieldRef.current?.querySelector('mud-select, select')),
        0,
      );
      return;
    }
    confirmMutation.reset();
    setConfirmChecked(false);
    setConfirmOpenedAt(new Date().toISOString());
    setConfirmOpen(true);
  }

  // FR-029, research R8: on success the draft is gone (use-draft invalidates
  // it), the toast names the report, and Acasă receives it as router state. The
  // count is the draft's own: an idempotent replay answers acceptedRows = 0.
  function saveReport(): void {
    if (!draft) return;
    const rowCount = draft.rows.length;
    const siteName = draftLocationName;
    confirmMutation.mutate(undefined, {
      onSuccess: (result) => {
        setConfirmOpen(false);
        toast({
          variant: 'success',
          title: t('stock_reporting.toast.saved.title'),
          text: t('stock_reporting.toast.saved.text', {
            number: result.reportNumber,
            count: rowCount,
            location: siteName,
          }),
        });
        const state: SavedReportState = {
          submissionId: result.submissionId,
          reportNumber: result.reportNumber,
          rowCount,
          siteName,
          savedAtIso: result.receivedAtIso,
        };
        navigate(`${rolePrefix}/home`, { state });
      },
    });
  }

  if (isError) {
    // A role without a registrar or location scope (a SYSTEM_ADMIN reaching
    // this page by URL) cannot report, and the page must say so.
    const noScope =
      loadFailureKind(error) === 'forbidden' ||
      (isNormalizedApiError(error) && /registrar scope/i.test(error.message ?? ''));
    return (
      <section className="simsm-stock-reporting">
        <MudInfoBox
          emphasis="strong"
          role={noScope ? 'status' : 'alert'}
          variant={noScope ? 'info' : 'error'}
        >
          {noScope ? t('stock_reporting.no_scope') : t('stock_reporting.load_error')}
        </MudInfoBox>
      </section>
    );
  }

  const locationOptions: SelectOption[] = locations.map((location) => ({
    value: location.id,
    label: location.internalCode ? `${location.name} · ${location.internalCode}` : location.name,
  }));
  const boundLocationName =
    locations.find((location) => location.id === siteId)?.name ?? siteId ?? '';
  const pendingLocationName =
    locations.find((location) => location.id === pendingSiteId)?.name ?? '';

  // FR-027 as the owner ruled it on 2026-09-15: rows, none with errors, nothing
  // mid-save. The location is not a condition of the button: pressing it with
  // none is refused on the field (openConfirmation), because a report with no
  // location would land in core.stock with no site_id, invisible in every
  // site-scoped stock view (caiet step 3). With a location, the draft must exist.
  // A change the server has not confirmed (waiting, in flight, or refused) has
  // not been validated either: the report is saved as the server holds it (T097).
  const canConfirm =
    rows.length > 0 && counts.withErrors === 0 && !unsaved && (siteId === null || Boolean(draft));

  const activeFilters = [
    criteria.errorsOnly
      ? {
          key: 'errors',
          label: t('stock_reporting.filters.show_only'),
          value: t('stock_reporting.filters.errors_only_plain'),
          onRemove: () => setCriteria((c) => ({ ...c, errorsOnly: false })),
        }
      : null,
    criteria.newLotsOnly
      ? {
          key: 'new-lots',
          label: t('stock_reporting.filters.show_only'),
          value: t('stock_reporting.filters.new_lots_plain'),
          onRemove: () => setCriteria((c) => ({ ...c, newLotsOnly: false })),
        }
      : null,
    criteria.correctionsOnly
      ? {
          key: 'corrections',
          label: t('stock_reporting.filters.show_only'),
          value: t('stock_reporting.filters.corrections_plain'),
          onRemove: () => setCriteria((c) => ({ ...c, correctionsOnly: false })),
        }
      : null,
  ].filter((f): f is NonNullable<typeof f> => f !== null);

  const narrowed = filteredRows.length !== counts.total;

  // The wireframe's columns (piese.tsx:1246), lot and expiry one fact in one
  // cell, plus the row's actions: seven, which decision D2 allows.
  const columns: TableColumn[] = [
    { key: 'medicine', label: t('stock_reporting.table.column.medicine') },
    { key: 'lot', label: t('stock_reporting.table.column.lot') },
    { key: 'previous', label: t('stock_reporting.table.column.previous'), align: 'end' },
    { key: 'current', label: t('stock_reporting.table.column.current'), align: 'end' },
    { key: 'difference', label: t('stock_reporting.table.column.difference'), align: 'end' },
    { key: 'reason', label: t('stock_reporting.table.column.reason') },
    { key: 'actions', label: t('stock_reporting.table.column.actions') },
  ];

  const tableRows = filteredRows.map((row) => ({
    id: row.clientRowId,
    medicine: row.medicineLabel ?? row.medicineCode ?? '',
    lot: row.lot ?? '',
    previous: row.previousQuantity ?? '',
    current: row.currentQuantity ?? '',
    difference: differenceOf(row) ?? '',
    reason: reasonLabel(row),
    actions: '',
  }));

  const previousLabel = (row: DraftRow): string => {
    if (row.lotMatch === 'NEW') return t('stock_reporting.table.previous_new');
    return typeof row.previousQuantity === 'number'
      ? formatNumber(row.previousQuantity)
      : EMPTY_CELL;
  };

  // "lot nou" or "corectare" (UC50-03, UC50-05); an existing lot is the rule and
  // carries no tag (piese.tsx:1139).
  const rowTag = (row: DraftRow): JSX.Element | null => {
    if (isCorrectionRow(row)) {
      return (
        <MudTag
          variant="status"
          size="sm"
          semantic="accent"
          label={t('stock_reporting.table.tag.correction')}
        />
      );
    }
    if (row.lotMatch === 'NEW') {
      return (
        <MudTag
          variant="status"
          size="sm"
          semantic="brand"
          label={t('stock_reporting.table.tag.new_lot')}
        />
      );
    }
    return null;
  };

  let emptySlot: JSX.Element | null = null;
  if (filteredRows.length === 0) {
    if (isLoading) {
      emptySlot = <p className="simsm-stock-reporting__loading">{t('stock_reporting.loading')}</p>;
    } else if (counts.total === 0) {
      emptySlot = (
        <EmptyState
          framed={false}
          icon="document"
          title={t('stock_reporting.empty.text')}
          body={t('stock_reporting.empty.body')}
          action={
            <MudButton shape="circular" variant="primary" size="md" onClick={openDialog}>
              <MudIcon slot="icon-start" name="plus-large" size={20} />
              {t('stock_reporting.empty.cta')}
            </MudButton>
          }
        />
      );
    } else {
      emptySlot = (
        <p className="simsm-stock-reporting__no-match">{t('stock_reporting.filters.no_match')}</p>
      );
    }
  }

  // FR-018, contracts/ui-flow.md §Page states: "Nou" until the draft exists,
  // then "Draft", with who started it and when. The author is the name the
  // draft recorded at creation (R7); a draft from before that falls back to
  // this session's name when it is this session's, else to the identifier.
  const sessionName = session ? `${session.firstName} ${session.lastName}`.trim() : '';
  const authorName =
    draft?.operatorName?.trim() ||
    (draft && session && draft.operatorIdnp === session.idnp ? sessionName : '') ||
    draft?.operatorIdnp ||
    '';
  const pageTitle = t(draftId ? 'stock_reporting.title_finalize' : 'stock_reporting.title');
  const subtitle = draft
    ? t('stock_reporting.header.sub.draft', {
        date: midSentenceDate(draft.createdAtIso),
        time: formatTime(draft.createdAtIso),
        author: authorName,
      })
    : t('stock_reporting.header.sub.new', { user: sessionName });

  return (
    <section className="simsm-stock-reporting">
      {draftId ? (
        <Breadcrumb
          label={t('common.breadcrumb.aria_label')}
          crumbs={[
            { label: t('drafts.title'), to: `${rolePrefix}/drafts` },
            { label: t('stock_reporting.title_finalize') },
          ]}
        />
      ) : null}
      <PageHeader
        title={
          <span className="simsm-page-header__title-status">
            {pageTitle}
            <MudTag
              variant="status"
              size="sm"
              semantic={draft ? 'brand' : 'neutral'}
              label={t(
                draft ? 'stock_reporting.header.tag.draft' : 'stock_reporting.header.tag.new',
              )}
            />
          </span>
        }
        lede={subtitle}
        actions={
          <div className="simsm-stock-reporting__header-side">
            {/* piese.tsx:34 ActiuniAntet: the Excel import beside the add action.
                Before them, caiet UC50 step 2's "Motive implicite", shut until
                the active Registrar's type, which decides its lists, is known. */}
            <div className="simsm-stock-reporting__header-actions">
              <MudButton
                shape="circular"
                appearance="text"
                variant="strict"
                size="md"
                disabled={defaults === null}
                onClick={() => {
                  setDefaultsKey((key) => key + 1);
                  setDefaultsOpen(true);
                }}
              >
                <MudIcon slot="icon-start" name="settings" size={20} />
                {t('stock_reporting.action.default_reasons')}
              </MudButton>
              <MudButton
                shape="circular"
                appearance="outlined"
                variant="strict"
                size="md"
                onClick={() => navigate(`${rolePrefix}/reporting/import`)}
              >
                <MudIcon slot="icon-start" name="upload" size={20} />
                {t('stock_reporting.action.import')}
              </MudButton>
              {/* The focus or the pointer leaving ends the handed-back focus's
                  quiet: the next visit is the reader asking again. Not while the
                  dialog is open — the button loses the focus to the dialog then,
                  and the quiet has to outlast that to cover its return. */}
              <span
                ref={addButtonRef}
                className="simsm-stock-reporting__add"
                onBlur={() => {
                  if (!dialogOpen) setQuietAddHint(false);
                }}
                onMouseLeave={() => {
                  if (!dialogOpen) setQuietAddHint(false);
                }}
              >
                <MudTooltip
                  content={t('stock_reporting.action.add_movement_tooltip')}
                  position="bottom"
                  disabled={quietAddHint}
                >
                  <MudButton
                    slot="trigger"
                    shape="circular"
                    variant="primary"
                    size="md"
                    aria-keyshortcuts="N"
                    onClick={openDialog}
                  >
                    <MudIcon slot="icon-start" name="plus-large" size={20} />
                    {t('stock_reporting.action.add_movement')}
                  </MudButton>
                </MudTooltip>
              </span>
            </div>
          </div>
        }
      />

      {draftMissing ? (
        <MudInfoBox emphasis="strong" role="status" variant="info">
          {t('stock_reporting.error.not_found.body')}
        </MudInfoBox>
      ) : null}

      {correctionFailure ? (
        <MudInfoBox emphasis="strong" role="alert" variant="error">
          {t(correctionFailure)}
        </MudInfoBox>
      ) : null}

      {/* R7, constitution art. III/IV: an edit another tab's removal left with
          no row is said here and offered again, never dropped in silence. */}
      {conflict ? (
        <MudInfoBox
          emphasis="strong"
          role="alert"
          variant="warning"
          titleText={t('stock_reporting.conflict.row_removed.title')}
        >
          {t('stock_reporting.conflict.row_removed.body', {
            medicine: conflict.row.medicineLabel || conflict.row.medicineCode || '',
            lot: conflict.row.lot ?? '',
          })}
          <MudButton
            slot="actions"
            shape="circular"
            appearance="text"
            variant="strict"
            size="sm"
            onClick={restoreConflict}
          >
            {t('stock_reporting.conflict.action.restore')}
          </MudButton>
          <MudButton
            slot="actions"
            shape="circular"
            appearance="text"
            variant="strict"
            size="sm"
            onClick={dismissConflict}
          >
            {t('stock_reporting.conflict.action.dismiss')}
          </MudButton>
        </MudInfoBox>
      ) : null}

      {/* A save that failed for any other reason is said too (art. IV); what
          it carried stays on screen and pending. */}
      {syncState.kind === 'error' ? (
        <MudInfoBox
          emphasis="strong"
          role="alert"
          variant="error"
          titleText={t('stock_reporting.save_failed.title')}
        >
          {t('stock_reporting.save_failed.body')}
          <MudButton
            slot="actions"
            shape="circular"
            appearance="text"
            variant="strict"
            size="sm"
            onClick={retrySave}
          >
            {t('stock_reporting.save_failed.action')}
          </MudButton>
        </MudInfoBox>
      ) : null}

      {/* The report card: toolbar, table, footer (piese.tsx:1310 `cadru`). */}
      <div className="simsm-stock-reporting__card">
        <div className="simsm-stock-reporting__toolbar">
          <div ref={locationFieldRef} className="simsm-stock-reporting__location">
            {/* AGE draws the required asterisk itself. Marked only once a save
                was refused for it: rows with no location are allowed until then. */}
            <MudSelect
              size="medium"
              required
              label={t('stock_reporting.location.label')}
              placeholder={t('stock_reporting.location.placeholder')}
              value={siteId ?? ''}
              disabled={locationsLoading || locationOptions.length === 0}
              invalid={saveNeedsLocation}
              {...(saveNeedsLocation ? { errorText: t('stock_reporting.location.required') } : {})}
              onMudChange={(e: CustomEvent<SelectChangeDetail>) => chooseSite(e.detail.value)}
            >
              {locationOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </MudSelect>
          </div>
          <FilterBar
            search={{
              value: criteria.query,
              onChange: (value) => setCriteria((c) => ({ ...c, query: value })),
              placeholder: t('stock_reporting.search.placeholder'),
              ariaLabel: t('stock_reporting.search.aria'),
              clearLabel: t('common.filter.clear_search'),
            }}
            active={activeFilters}
            onReset={() => setCriteria(NO_CRITERIA)}
            panelWidth={300}
            labels={{
              filters: t('common.filter.button'),
              reset: t('stock_reporting.filters.clear'),
              panelAriaLabel: t('stock_reporting.filters.panel_aria'),
              activeAriaLabel: t('common.filter.active_aria_label'),
            }}
            panel={
              <>
                <span className="simsm-filter-bar__field-label">
                  {t('stock_reporting.filters.show_only')}
                </span>
                <MudCheckbox
                  size="md"
                  checked={criteria.errorsOnly}
                  onMudChange={(e: CustomEvent<{ checked: boolean }>) =>
                    setCriteria((c) => ({ ...c, errorsOnly: e.detail.checked }))
                  }
                >
                  <span slot="label">{t('stock_reporting.filters.errors_only_plain')}</span>
                </MudCheckbox>
                <MudCheckbox
                  size="md"
                  checked={criteria.newLotsOnly}
                  onMudChange={(e: CustomEvent<{ checked: boolean }>) =>
                    setCriteria((c) => ({ ...c, newLotsOnly: e.detail.checked }))
                  }
                >
                  <span slot="label">{t('stock_reporting.filters.new_lots_plain')}</span>
                </MudCheckbox>
                <MudCheckbox
                  size="md"
                  checked={criteria.correctionsOnly}
                  onMudChange={(e: CustomEvent<{ checked: boolean }>) =>
                    setCriteria((c) => ({ ...c, correctionsOnly: e.detail.checked }))
                  }
                >
                  <span slot="label">{t('stock_reporting.filters.corrections_plain')}</span>
                </MudCheckbox>
              </>
            }
          />
        </div>

        {/* A read that failed is said as such: "no location" would tell the
            operator to ask for one that may well exist (§Decision 8). */}
        {locationsFailed && locations.length === 0 ? (
          <MudInfoBox
            emphasis="strong"
            role="alert"
            variant="error"
            titleText={t('stock_reporting.location.load_error.title')}
          >
            {t('stock_reporting.location.load_error.body')}
          </MudInfoBox>
        ) : !locationsLoading && locationOptions.length === 0 ? (
          <MudInfoBox
            emphasis="strong"
            variant="warning"
            titleText={t('stock_reporting.location.none.title')}
          >
            {t('stock_reporting.location.none.body')}
          </MudInfoBox>
        ) : null}

        {siteHasDraftNotice ? (
          // Appears after the reader picked a location that already had a report
          // (or arrived from one), so it is announced: AGE's notification put
          // role="status" on an info itself, and mud-info-box has none (059 V-087).
          <MudInfoBox emphasis="strong" role="status" variant="info">
            {t('stock_reporting.error.site_has_draft.body')}
          </MudInfoBox>
        ) : null}

        <MudTable
          className="simsm-table simsm-report-table"
          columns={columns}
          rows={tableRows}
          rowStyle="divided"
          disableSort={true}
          aria-label={t('stock_reporting.table.aria')}
        >
          {emptySlot ? (
            <div slot="empty" className="simsm-report-table__empty">
              {emptySlot}
            </div>
          ) : null}
          <span slot="header-cell-actions" className="visually-hidden">
            {t('stock_reporting.table.column.actions')}
          </span>
          {filteredRows.map((row, index) => {
            const diff = differenceOf(row);
            return [
              // Row messages go under the medicine (FR-020; piese.tsx:1158).
              <span
                key={`medicine-${row.clientRowId}`}
                slot={`cell-medicine-${index}`}
                className="simsm-report-table__medicine"
              >
                <span className="simsm-report-table__name">
                  {row.medicineLabel || row.medicineCode || EMPTY_CELL}
                </span>
                {row.medicineLabel && row.medicineCode ? (
                  <span className="simsm-report-table__code">{row.medicineCode}</span>
                ) : null}
                {row.validationErrors.map((issue, i) => (
                  <MudInlineMessage key={`${issue.code}-${i}`} variant="error" size="small">
                    {rowIssueMessage(t, issue, row)}
                  </MudInlineMessage>
                ))}
              </span>,
              <span
                key={`lot-${row.clientRowId}`}
                slot={`cell-lot-${index}`}
                className="simsm-report-table__lot"
              >
                <span className="simsm-report-table__lot-line">
                  <span className="simsm-report-table__lot-number">{row.lot || EMPTY_CELL}</span>
                  {rowTag(row)}
                </span>
                <span className="simsm-report-table__expiry">
                  {row.lotExpiry
                    ? formatExpiry(row.lotExpiry, rowExpiryPrecision(row))
                    : EMPTY_CELL}
                </span>
                {/* UC50-05: what the correction replaces, by report and channel. */}
                {row.correctionOf ? <CorrectionReference movementId={row.correctionOf} /> : null}
              </span>,
              <span
                key={`previous-${row.clientRowId}`}
                slot={`cell-previous-${index}`}
                className="simsm-report-table__num"
              >
                {previousLabel(row)}
              </span>,
              <span
                key={`current-${row.clientRowId}`}
                slot={`cell-current-${index}`}
                className="simsm-report-table__num simsm-report-table__num--strong"
              >
                {typeof row.currentQuantity === 'number'
                  ? formatNumber(row.currentQuantity)
                  : EMPTY_CELL}
              </span>,
              <span
                key={`difference-${row.clientRowId}`}
                slot={`cell-difference-${index}`}
                className={
                  diff === null || diff === 0
                    ? 'simsm-report-table__num'
                    : `simsm-report-table__num simsm-report-table__num--${diff > 0 ? 'up' : 'down'}`
                }
              >
                {diff === null
                  ? EMPTY_CELL
                  : diff > 0
                    ? `+${formatNumber(diff)}`
                    : formatNumber(diff)}
              </span>,
              <span key={`reason-${row.clientRowId}`} slot={`cell-reason-${index}`}>
                {reasonLabel(row) || EMPTY_CELL}
              </span>,
              // FR-020: the row opens in the same dialog, or leaves the draft.
              <span
                key={`actions-${row.clientRowId}`}
                slot={`cell-actions-${index}`}
                className="simsm-report-table__actions"
                data-row-actions={row.clientRowId}
              >
                <RowActionsMenu
                  label={t('stock_reporting.row.actions_aria')}
                  align="end"
                  actions={[
                    {
                      label: t('stock_reporting.row.action.edit'),
                      onSelect: () => openEdit(row.clientRowId),
                    },
                    {
                      label: t('stock_reporting.row.action.remove'),
                      danger: true,
                      onSelect: () => removeRow(row.clientRowId),
                    },
                  ]}
                />
              </span>,
            ];
          })}
        </MudTable>

        {/* piese.tsx:1434 SubsolRaport: what the report holds, then its two
            actions. "Salvează raport" is the confirm gate, not a draft save. */}
        <div
          className="simsm-stock-reporting__footer"
          role="group"
          aria-label={t('stock_reporting.actions.aria')}
        >
          <span className="simsm-stock-reporting__count">
            {narrowed
              ? t('stock_reporting.footer.filtered', {
                  shown: filteredRows.length,
                  total: counts.total,
                })
              : t('stock_reporting.footer.rows', { count: counts.total })}
            {counts.withErrors > 0 ? (
              <>
                {' · '}
                <span className="simsm-stock-reporting__count-errors">
                  {t('stock_reporting.footer.with_errors', { count: counts.withErrors })}
                </span>
              </>
            ) : null}
            {narrowed ? (
              <>
                {' · '}
                <MudButton
                  shape="circular"
                  appearance="text"
                  variant="strict"
                  size="sm"
                  onClick={() => setCriteria(NO_CRITERIA)}
                >
                  {t('stock_reporting.footer.show_all')}
                </MudButton>
              </>
            ) : null}
          </span>
          <div className="simsm-stock-reporting__footer-actions">
            {/* FR-021: it asks first, naming how many rows go (owner decision,
                2026-09-14; the wireframe empties on one click). */}
            <MudButton
              shape="circular"
              appearance="text"
              variant="strict"
              size="lg"
              onClick={() => {
                discardMutation.reset();
                setClearOpen(true);
              }}
              disabled={counts.total === 0}
            >
              {t('stock_reporting.action.clear_report')}
            </MudButton>
            {/* FR-027: why it is disabled, said beside it (research R12). */}
            {counts.withErrors > 0 ? (
              <span className="simsm-stock-reporting__action-hint">
                {t('stock_reporting.save.blocked_errors')}
              </span>
            ) : null}
            <MudButton
              shape="circular"
              variant="primary"
              size="lg"
              disabled={!canConfirm}
              onClick={openConfirmation}
            >
              {t('stock_reporting.action.save_report')}
            </MudButton>
          </div>
        </div>
      </div>

      {errorRowNotice(counts.withErrors, t)}

      {/* Beside the footer, where "Salvează raport" was pressed. */}
      {saveNeedsLocation ? (
        <MudInfoBox
          emphasis="strong"
          role="alert"
          variant="error"
          titleText={t('stock_reporting.save.needs_location.title')}
        >
          {t('stock_reporting.save.needs_location.body')}
        </MudInfoBox>
      ) : null}

      {dialogOpen ? (
        <StockMovementDialog
          key={dialogKey}
          open
          siteId={siteId}
          rows={rows}
          defaults={defaults}
          defaultDirection={defaultDirection}
          registrarKind={registrarKind}
          registrarKindState={registrarKindState}
          editing={editingRow}
          prefill={prefill ?? undefined}
          correction={correcting ?? undefined}
          onAdd={onMovementAdded}
          onEditRow={openEdit}
          onClose={closeDialog}
        />
      ) : null}

      {/* Caiet UC50 step 2: what new movements start from. Saved with the
          draft's next save, or with the save after its first row creates it. */}
      {defaults && registrarKind ? (
        <DefaultReasonsDialog
          key={defaultsKey}
          open={defaultsOpen}
          defaults={defaults}
          direction={defaultDirection}
          registrarKind={registrarKind}
          onApply={(next) => {
            rememberDefaultReasons(next);
            setDefaultsOpen(false);
          }}
          onClose={() => setDefaultsOpen(false)}
        />
      ) : null}

      {/* FR-021, owner decision 2026-09-14: "Golește raportul" asks first, and
          confirming deletes the draft rather than leaving it empty (R7). */}
      <MudModal
        closeLabel={t('common.close')}
        open={clearOpen}
        titleText={t('stock_reporting.clear.title')}
        // One sentence and two actions: MUD's Small modal (`mud/components/
        // modal.md`, 588 × 184 — header 56, "Text Small" 56, footer 72), which
        // AGE's `sm` reproduces in height. AGE draws it 480 wide, not 588.
        size="sm"
        destructive
        onMudClose={onModalDismiss(() => setClearOpen(false))}
      >
        <div className="simsm-stock-reporting__confirm">
          <p>{t('stock_reporting.clear.body', { count: counts.total })}</p>
          {discardMutation.isError ? (
            <MudInfoBox emphasis="strong" role="alert" variant="error">
              {t(apiErrorKey(discardMutation.error, 'stock_reporting.clear.error'))}
            </MudInfoBox>
          ) : null}
        </div>
        <div slot="actions" className="simsm-stock-reporting__confirm-actions">
          <MudButton
            shape="circular"
            appearance="text"
            variant="strict"
            size="md"
            onClick={() => setClearOpen(false)}
          >
            {t('stock_reporting.clear.action.cancel')}
          </MudButton>
          <MudButton
            shape="circular"
            variant="destructive"
            size="md"
            disabled={discardMutation.isPending}
            onClick={() =>
              discardMutation.mutate(undefined, {
                onSuccess: () => {
                  setClearOpen(false);
                  // The draft this address named is gone: the location's report
                  // starts again "Nou", at its own address.
                  if (draftId && siteId) openSite(siteId);
                },
              })
            }
          >
            {t('stock_reporting.clear.action.confirm')}
          </MudButton>
        </div>
      </MudModal>

      <MudModal
        closeLabel={t('common.close')}
        open={pendingSiteId !== null}
        titleText={t('stock_reporting.location.switch.title')}
        size="sm"
        onMudClose={onModalDismiss(() => setPendingSiteId(null))}
      >
        <p>
          {t('stock_reporting.location.switch.body', {
            count: counts.total,
            from: boundLocationName,
            to: pendingLocationName,
          })}
        </p>
        <div slot="actions" className="simsm-stock-reporting__confirm-actions">
          <MudButton
            shape="circular"
            variant="neutral"
            size="md"
            onClick={() => setPendingSiteId(null)}
          >
            {t('stock_reporting.location.switch.action.cancel')}
          </MudButton>
          <MudButton
            shape="circular"
            variant="primary"
            size="md"
            onClick={() => {
              const next = pendingSiteId;
              setPendingSiteId(null);
              if (next) openSite(next);
            }}
          >
            {t('stock_reporting.location.switch.action.confirm')}
          </MudButton>
        </div>
      </MudModal>

      <MudModal
        closeLabel={t('common.close')}
        open={confirmOpen}
        titleText={t('stock_reporting.confirm.title')}
        size="md"
        // AGE-BUG(mudClose-bubbles) · vendor/age-design/SOURCE_COMMIT 7322a157 — seven AGE
        // components emit `mudClose` bubbling+composed, so a descendant closing inside this
        // modal reaches this handler too; the guard answers only a real dismissal.
        onMudClose={onModalDismiss(() => setConfirmOpen(false))}
      >
        {/* contracts/ui-flow.md §Save confirmation (FR-028): what is about to be
            saved, then the explicit confirmation caiet UC50-10 requires. The
            result is Acasă and a toast, never a box inside this modal (FR-029). */}
        <div className="simsm-stock-reporting__confirm">
          <p className="simsm-stock-reporting__confirm-meta">{t('stock_reporting.confirm.body')}</p>
          <dl className="simsm-stock-reporting__summary">
            <div>
              <dt>{t('stock_reporting.confirm.summary.location')}</dt>
              <dd>{draftLocationName || EMPTY_CELL}</dd>
            </div>
            <div>
              <dt>{t('stock_reporting.confirm.summary.movements')}</dt>
              <dd>{formatNumber(draft?.rows.length ?? 0)}</dd>
            </div>
            <div>
              <dt>{t('stock_reporting.confirm.summary.reported_by')}</dt>
              <dd>{reporterName || EMPTY_CELL}</dd>
            </div>
            <div>
              <dt>{t('stock_reporting.confirm.summary.saved_at')}</dt>
              <dd>
                {t('stock_reporting.confirm.summary.saved_at_value', {
                  date: fullDate(confirmOpenedAt),
                  time: formatTime(confirmOpenedAt),
                })}
              </dd>
            </div>
          </dl>
          <MudCheckbox
            size="md"
            checked={confirmChecked}
            onMudChange={(e: CustomEvent<{ checked: boolean }>) =>
              setConfirmChecked(e.detail.checked)
            }
          >
            <span slot="label">{t('stock_reporting.confirm.checkbox')}</span>
          </MudCheckbox>
          {confirmMutation.isError ? (
            <MudInfoBox
              emphasis="strong"
              role="alert"
              variant="error"
              titleText={t('stock_reporting.error.generic.title')}
            >
              {t(confirmErrorKey(confirmMutation.error))}
            </MudInfoBox>
          ) : null}
        </div>
        <div slot="actions" className="simsm-stock-reporting__confirm-actions">
          {/* Why "Salvează raportul" is disabled, beside it (research R12). */}
          {confirmChecked ? null : (
            <span className="simsm-stock-reporting__action-hint">
              {t('stock_reporting.confirm.hint.unchecked')}
            </span>
          )}
          <MudButton
            shape="circular"
            appearance="text"
            variant="strict"
            size="md"
            onClick={() => setConfirmOpen(false)}
          >
            {t('stock_reporting.confirm.action.cancel')}
          </MudButton>
          <MudButton
            shape="circular"
            variant="primary"
            size="md"
            disabled={!confirmChecked || confirmMutation.isPending}
            onClick={saveReport}
          >
            {t('stock_reporting.confirm.action.confirm')}
          </MudButton>
        </div>
      </MudModal>
    </section>
  );
}

function errorRowNotice(withErrors: number, t: TFn): JSX.Element | null {
  if (withErrors === 0) return null;
  return (
    <MudInfoBox
      emphasis="strong"
      role="alert"
      variant="warning"
      titleText={t('stock_reporting.errors.title')}
    >
      {/* The filter named as its checkbox is labelled (T098). */}
      {t('stock_reporting.errors.body', {
        count: withErrors,
        filter: t('stock_reporting.filters.errors_only_plain'),
      })}
    </MudInfoBox>
  );
}
